import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ViewHeader, PortalEmptyState } from "./PortalShared";
import { ClipboardList, CheckCircle, Circle, RefreshCw, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ProgressBar({ progress }) {
  return (
    <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
      <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
    </div>
  );
}

function ChecklistDetail({ checklist, onBack, onProgressUpdate }) {
  const [tasks, setTasks] = useState(checklist.tasks || []);
  const [updating, setUpdating] = useState(null);
  const categories = [...new Set(tasks.map(t => t.category))];

  const toggleTask = async (index) => {
    setUpdating(index);
    const newVal = !tasks[index].completed;
    try {
      const res = await axios.patch(
        `${BACKEND_URL}/api/portal-tools/closing-checklists/${checklist.checklist_id}/task`,
        { task_index: index, completed: newVal },
        { withCredentials: true }
      );
      setTasks(prev => prev.map((t, i) => i === index ? { ...t, completed: newVal } : t));
      if (onProgressUpdate) onProgressUpdate(checklist.checklist_id, res.data.progress);
      toast.success(newVal ? "Task completed" : "Task unchecked");
    } catch { toast.error("Failed to update"); }
    finally { setUpdating(null); }
  };

  const completed = tasks.filter(t => t.completed).length;
  const progress = tasks.length > 0 ? Math.round((completed / tasks.length) * 100) : 0;

  return (
    <div>
      <ViewHeader title={checklist.property_address} subtitle={`${checklist.buyer_name} / ${checklist.seller_name} · Closing ${checklist.closing_date ? new Date(checklist.closing_date).toLocaleDateString() : 'TBD'}`} action="Back" onAction={onBack} />
      <div className="mb-6 bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white/50 text-sm">Closing Progress</span>
          <span className="text-emerald-400 font-bold text-sm">{completed}/{tasks.length} ({progress}%)</span>
        </div>
        <ProgressBar progress={progress} />
      </div>
      {categories.map(cat => (
        <div key={cat} className="mb-4">
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-2">{cat}</p>
          <div className="space-y-1.5">
            {tasks.map((task, i) => task.category === cat && (
              <button key={i} onClick={() => toggleTask(i)} disabled={updating === i}
                data-testid={`closing-task-${i}`}
                className="w-full flex items-center gap-3 p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg hover:border-emerald-500/20 transition-all text-left">
                {updating === i ? <RefreshCw className="w-4 h-4 text-emerald-400 animate-spin flex-shrink-0" /> :
                  task.completed ? <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" /> :
                    <Circle className="w-4 h-4 text-white/20 flex-shrink-0" />}
                <span className={`text-sm ${task.completed ? "line-through text-white/30" : "text-white/70"}`}>{task.task}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ClosingChecklistManager({ checklists: initial = [] }) {
  const [checklists, setChecklists] = useState(initial);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    property_address: "", buyer_name: "", seller_name: "",
    purchase_price: "", closing_date: "", lender_name: "", loan_amount: ""
  });

  const handleCreate = async () => {
    if (!form.property_address || !form.buyer_name || !form.seller_name) {
      toast.error("Property address, buyer and seller name required"); return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal-tools/closing-checklists`, {
        ...form,
        purchase_price: parseFloat(form.purchase_price) || 0,
        loan_amount: parseFloat(form.loan_amount) || 0,
      }, { withCredentials: true });
      setChecklists(prev => [res.data, ...prev]);
      toast.success("Closing checklist created");
      setShowForm(false);
      setForm({ property_address: "", buyer_name: "", seller_name: "", purchase_price: "", closing_date: "", lender_name: "", loan_amount: "" });
    } catch { toast.error("Failed to create checklist"); }
    finally { setSaving(false); }
  };

  const updateProgress = (id, progress) => {
    setChecklists(prev => prev.map(c => c.checklist_id === id ? { ...c, progress } : c));
  };

  const selectedChecklist = checklists.find(c => c.checklist_id === selected);
  if (selectedChecklist) return <ChecklistDetail checklist={selectedChecklist} onBack={() => setSelected(null)} onProgressUpdate={updateProgress} />;

  if (showForm) return (
    <div>
      <ViewHeader title="New Closing Checklist" subtitle="Track all tasks for a new closing" action="Back" onAction={() => setShowForm(false)} />
      <div className="max-w-lg bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-6 space-y-4">
        {[
          ["property_address","Property Address"],["buyer_name","Buyer Name"],["seller_name","Seller Name"],
          ["lender_name","Lender Name (optional)"]
        ].map(([k,l]) => (
          <div key={k}>
            <label className="text-[10px] text-white/40 block mb-1">{l}</label>
            <Input value={form[k]} onChange={e => setForm(f=>({...f,[k]:e.target.value}))} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
          </div>
        ))}
        {[["purchase_price","Purchase Price","number"],["loan_amount","Loan Amount","number"],["closing_date","Target Closing Date","date"]].map(([k,l,t]) => (
          <div key={k}>
            <label className="text-[10px] text-white/40 block mb-1">{l}</label>
            <Input type={t} value={form[k]} onChange={e => setForm(f=>({...f,[k]:e.target.value}))} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
          </div>
        ))}
        <p className="text-[10px] text-white/30">A standard 16-task closing checklist (Title, Survey, Lender, Closing, Recording) will be generated automatically.</p>
        <Button onClick={handleCreate} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="create-closing-btn">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Create Checklist
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      <ViewHeader title="Closing Checklists" subtitle="Track every closing task from title to recording" action="New Checklist" onAction={() => setShowForm(true)} />
      {checklists.length === 0 ? (
        <PortalEmptyState icon={ClipboardList} title="No Closing Checklists" desc="Create a checklist to track each closing from start to finish" action="New Checklist" onAction={() => setShowForm(true)} />
      ) : (
        <div className="space-y-3">
          {checklists.map(cl => (
            <div key={cl.checklist_id} onClick={() => setSelected(cl.checklist_id)}
              data-testid={`closing-row-${cl.checklist_id}`}
              className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 cursor-pointer hover:border-emerald-500/30 transition-all">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-white font-medium text-sm">{cl.property_address}</p>
                  <p className="text-white/40 text-xs">
                    {cl.buyer_name} / {cl.seller_name}
                    {cl.closing_date && <> · Closing {new Date(cl.closing_date).toLocaleDateString()}</>}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-bold ${cl.progress >= 100 ? 'text-emerald-400' : 'text-[#D4AF37]'}`}>{cl.progress || 0}%</span>
                  {cl.purchase_price && <p className="text-white/30 text-xs">${(cl.purchase_price||0).toLocaleString()}</p>}
                </div>
              </div>
              <ProgressBar progress={cl.progress || 0} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
